import { useState, useEffect } from 'react';
import { buildingService, propertyService } from 'services/api';
import PropertyCombobox from 'components/PropertyCombobox';
import { useAuth } from 'context/AuthContext';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import GrupoEncabezado from 'components/GrupoEncabezado';
import BuildingFormModal from './BuildingFormModal';
import AsambleaEnCursoAviso from 'components/AsambleaEnCursoAviso';
import Paginacion from 'components/Paginacion';
import styles from './Buildings.module.css';

export default function Buildings() {
  const { isSuperAdmin, user, getSubscriptionStateForProperty } = useAuth();
  const isAdminMultiProp = user?.role === 'admin';

  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [alert,      setAlert]      = useState(null);
  const [show,       setShow]       = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [properties, setProperties] = useState([]);
  const [collapsed,  setCollapsed]  = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [filterProp,      setFilterProp]      = useState('');
  const [selectedPropObj, setSelectedPropObj] = useState(null);
  const [propsLoaded,     setPropsLoaded]     = useState(false);
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const LIMIT = 50;

  // Estado de suscripción de la propiedad seleccionada
  const effectiveSub = selectedPropObj ? getSubscriptionStateForProperty(selectedPropObj) : 'none';
  const isReadOnly   = effectiveSub === 'grace';
  const isBlocked    = effectiveSub === 'blocked';

  // Carga paginada de torres de una propiedad — compartida entre la
  // auto-selección inicial, el selector de propiedad y el reload post-guardado.
  const irAPagina = (n) => { loadBuildings(filterProp, n); window.scrollTo({ top: 0 }); };

  const loadBuildings = (propId, p = 1) => {
    setLoading(true);
    buildingService.getAll({ property_id: propId, page: p, limit: LIMIT })
      .then(r => {
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
        setTotalUnits(r.data.totalUnits || 0);
        setPage(r.data.page || 1);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // Cargar propiedades para el selector
  useEffect(() => {
    if (isSuperAdmin || isAdminMultiProp) {
      propertyService.selector({ limit: 200 }).then(r => {
        const props = r.data.data || [];
        setProperties(props);
        setPropsLoaded(true);
        // Si admin tiene solo 1 propiedad → auto-seleccionarla y cargar datos
        if (isAdminMultiProp && !isSuperAdmin && props.length === 1) {
          setFilterProp(String(props[0].id));
          setSelectedPropObj(props[0]);
          loadBuildings(props[0].id, 1);
        }
      });
    }
  }, [isSuperAdmin, isAdminMultiProp]);

  // Super_admin / admin multi-prop: cargar torres al seleccionar propiedad
  const handleFilterProp = (propId, elegida) => {
    const propObj = elegida || properties.find(p => String(p.id) === String(propId)) || null;
    setFilterProp(propId);
    setSelectedPropObj(propObj);
    setItems([]);
    if (!propId) return;
    loadBuildings(propId, 1);
  };

  const reload = () => {
    if (filterProp) loadBuildings(filterProp, page);
  };

  const del = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await buildingService.delete(confirmDel.id);
      setAlert({ type: 'success', message: 'Torre eliminada' });
      setConfirmDel(null);
      reload();
    } catch (err) { setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al eliminar' }); setConfirmDel(null); }
    finally { setDeleting(false); }
  };

  const openEdit = (b) => { setEditing(b); setShow(true); };
  const close      = () => { setShow(false); setEditing(null); };
  const toggleProp = (id) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

  const selectedPropName = selectedPropObj?.name || '';
  // Multi-prop: necesita selección. 1 prop o super_admin con selección: ya tiene datos
  const needsSelection = propsLoaded && (isSuperAdmin || (isAdminMultiProp && properties.length > 1)) && !filterProp;
  // Mostrar spinner mientras carga las propiedades del admin
  const loadingProps = isAdminMultiProp && !isSuperAdmin && !propsLoaded;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-layer-group ${styles.iconoTitulo}`} />Torres</h1>
          <p>{needsSelection ? 'Selecciona una propiedad para ver sus torres' : selectedPropName ? `Torres de ${selectedPropName}` : 'Torres de tu propiedad'}</p>
        </div>
        <div className={styles.accionesEncabezado}>

          {/* Selector de propiedad — super_admin y admin con >1 propiedad */}
          {(isSuperAdmin || (isAdminMultiProp && properties.length > 1)) && (
            <div className={styles.filaSelector}>
              <i className={`fas fa-building ${styles.iconoSelector}`} />
              <PropertyCombobox value={filterProp} onChange={handleFilterProp} placeholder="— Selecciona una propiedad —" />
              {filterProp && !isSuperAdmin && (
                <button className="btn btn-sm btn-secondary btn-icon" onClick={() => handleFilterProp('')} title="Limpiar">
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          )}

          {/* Botón nueva torre */}
          {!isReadOnly && !isBlocked && !needsSelection && !loadingProps && (
            <button className="btn btn-primary"
              onClick={() => { setEditing(null); setShow(true); }}>
              <i className="fas fa-plus" /> Nueva torre
            </button>
          )}
        </div>
      </div>

      {alert && <div className={`alert alert-${alert.type} ${styles.alertaClic}`} onClick={() => setAlert(null)}>{alert.message}</div>}

      {(filterProp || !(isSuperAdmin || isAdminMultiProp)) && <AsambleaEnCursoAviso propertyId={filterProp} enTorres />}

      {/* Banners de membresía */}
      {isBlocked && <div className={`${styles.banner} ${styles.bannerBloqueada}`}>
        <i className={`fas fa-ban ${styles.bannerIcono}`} />
        <span><strong>Membresía bloqueada.</strong> No puedes gestionar torres de {selectedPropName} hasta renovar.</span>
      </div>}
      {isReadOnly && !isBlocked && <div className={`${styles.banner} ${styles.bannerSoloLectura}`}>
        <i className={`fas fa-lock ${styles.bannerIcono}`} />
        <span><strong>Solo lectura.</strong> La membresía de {selectedPropName} está en período de gracia.</span>
      </div>}

      {/* Espera carga inicial */}
      {loadingProps && <div className="loading"><div className="spinner" /></div>}

      {/* Espera selección */}
      {needsSelection && <div className="card"><div className="empty-state">
        <i className={`fas fa-building ${styles.vacioIcono}`} />
        <p className={styles.vacioTexto}>Selecciona una propiedad para ver sus torres</p>
      </div></div>}

      {!needsSelection && !loadingProps && loading && <div className="loading"><div className="spinner" /></div>}

      {!needsSelection && !loadingProps && !loading && items.length === 0 && filterProp && (
        <div className="card"><div className="empty-state">
          <i className="fas fa-layer-group" />
          <p>Sin torres para {selectedPropName}</p>
        </div></div>
      )}

      {/* Tabla de torres */}
      {!needsSelection && !loadingProps && !loading && items.length > 0 && (
        <div className={styles.tablaCaja}>
          <GrupoEncabezado
            icono="building"
            titulo={selectedPropName}
            cifras={[{ etiqueta: 'Torres', valor: total }, { etiqueta: 'Unidades', valor: totalUnits }]}
            abierto={!collapsed['main']}
            onAlternar={() => toggleProp('main')}
          />
          {!collapsed['main'] && (
            <div className="table-wrapper">
              <table className={styles.tablaFondo}>
                <thead>
                  <tr>
                    <th>ID</th><th>Nombre</th><th>Unidades</th>
                    {!isReadOnly && !isBlocked && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map(b => (
                    <tr key={b.id}>
                      <td className={styles.celdaId}>#{b.id}</td>
                      <td><strong>{b.numero_torre}</strong></td>
                      <td><span className="badge badge-secondary">{b.units_count ?? 0} unidad{(b.units_count ?? 0) !== 1 ? 'es' : ''}</span></td>
                      {!isReadOnly && !isBlocked && (
                        <td>
                          <div className="actions">
                            <button className="btn btn-sm btn-secondary btn-icon" onClick={() => openEdit(b)} title="Editar"><i className="fas fa-pencil" /></button>
                            <button className="btn btn-sm btn-danger btn-icon"
                              onClick={() => setConfirmDel({ id: b.id, nombre: b.numero_torre, propiedad: selectedPropName, unitsCount: b.units_count ?? 0 })} title="Eliminar">
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {!loading && !needsSelection && !collapsed['main'] && <Paginacion pagina={page} total={total} porPagina={LIMIT} onCambiar={irAPagina} />}

      {/* Modal crear/editar */}
      {show && (
        <BuildingFormModal
          editing={editing}
          properties={properties}
          isSuperAdmin={isSuperAdmin}
          defaultPropertyId={filterProp}
          onClose={close}
          onSaved={(msg) => {
            setShow(false); setEditing(null);
            setAlert({ type: 'success', message: msg });
            reload();
          }}
        />
      )}

      {/* Modal confirmación eliminar */}
      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar torre" onClose={() => setConfirmDel(null)} onConfirm={del} deleting={deleting}>
          <div className={styles.eliminarPregunta}>
            ¿Eliminar <span className={styles.eliminarNombre}>{confirmDel.nombre}</span>?
          </div>
          <div className={styles.eliminarDetalle}>
            {confirmDel.propiedad && <><strong>{confirmDel.propiedad}</strong> · </>}
            Se eliminarán también {confirmDel.unitsCount > 0 ? <>sus <strong>{confirmDel.unitsCount} unidad{confirmDel.unitsCount !== 1 ? 'es' : ''}</strong></> : 'sus unidades'}.
            Los propietarios que se queden sin ninguna otra unidad en el sistema perderán el acceso a su cuenta (no se elimina como persona), y sus asistencias y votos históricos en esas unidades quedan sin unidad asociada.
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}